import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock } from 'lucide-react'
import './TimePicker.css'

const HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'))
const MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'))

function currentParts() {
  const now = new Date()
  return [String(now.getHours()).padStart(2, '0'), String(now.getMinutes()).padStart(2, '0')]
}

function splitTime(value) {
  const match = String(value || '').match(/^(\d{2}):(\d{2})/)
  if (!match) return currentParts()
  const hour = Math.min(23, Number(match[1]))
  const minute = Math.min(59, Number(match[2]))
  return [String(hour).padStart(2, '0'), String(minute).padStart(2, '0')]
}

function TimePicker({
  value,
  onChange,
  placeholder = 'Selecionar hora',
  title = 'Hora',
  disabled = false,
}) {
  const [open, setOpen] = useState(false)
  const [narrow, setNarrow] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 280, maxHeight: 0 })
  const triggerRef = useRef(null)
  const hourRef = useRef(null)
  const minuteRef = useRef(null)
  const [hour, minute] = splitTime(value)
  const displayValue = value ? `${hour}:${minute}` : placeholder

  useLayoutEffect(() => {
    if (!open) return undefined

    const updatePosition = () => {
      const element = triggerRef.current
      if (!element) return
      const rect = element.getBoundingClientRect()
      const dropdown = window.matchMedia('(max-width: 768px)').matches
      const width = dropdown
        ? Math.min(Math.max(rect.width, 240), window.innerWidth - 16)
        : Math.min(280, window.innerWidth - 16)
      const estimatedHeight = dropdown ? Math.min(320, window.innerHeight - 16) : 360
      const gap = 8
      let top = rect.bottom + gap
      let left = rect.left
      if (top + estimatedHeight > window.innerHeight - gap) {
        top = Math.max(gap, rect.top - estimatedHeight - gap)
      }
      if (left + width > window.innerWidth - gap) left = window.innerWidth - width - gap
      if (left < gap) left = gap
      const maxHeight = dropdown
        ? Math.max(200, Math.min(estimatedHeight, window.innerHeight - top - gap))
        : 0
      setPosition({ top, left, width, maxHeight })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)')
    const apply = () => setNarrow(media.matches)
    apply()
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [])

  useEffect(() => {
    if (!open) return undefined
    hourRef.current?.scrollIntoView({ block: 'center' })
    minuteRef.current?.scrollIntoView({ block: 'center' })
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, hour, minute])

  const apply = (nextHour, nextMinute) => {
    onChange?.(`${nextHour}:${nextMinute}`)
  }

  const portal = open ? (
    <>
      <div className="erp-time-picker-backdrop" onClick={() => setOpen(false)} aria-hidden />
      <div
        className={`erp-time-picker-popover${narrow ? ' is-dropdown' : ''}`}
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          ...(narrow && position.maxHeight ? { maxHeight: position.maxHeight } : {}),
        }}
        role="dialog"
        aria-label={title}
      >
        <div className="erp-time-picker-head">
          <Clock size={16} />
          <div>
            <strong>{title}</strong>
            <span>{hour}:{minute}</span>
          </div>
          <button
            type="button"
            className="erp-time-picker-now"
            onClick={() => {
              const [nowHour, nowMinute] = currentParts()
              apply(nowHour, nowMinute)
              setOpen(false)
            }}
          >
            Agora
          </button>
        </div>
        <div className="erp-time-picker-columns">
          <div className="erp-time-picker-column" role="listbox" aria-label="Hora">
            {HOURS.map((item) => (
              <button
                key={item}
                ref={item === hour ? hourRef : null}
                type="button"
                role="option"
                aria-selected={item === hour}
                className={item === hour ? 'is-selected' : ''}
                onClick={() => apply(item, minute)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="erp-time-picker-column" role="listbox" aria-label="Minuto">
            {MINUTES.map((item) => (
              <button
                key={item}
                ref={item === minute ? minuteRef : null}
                type="button"
                role="option"
                aria-selected={item === minute}
                className={item === minute ? 'is-selected' : ''}
                onClick={() => {
                  apply(hour, item)
                  setOpen(false)
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  ) : null

  return (
    <div className="erp-time-picker">
      <button
        ref={triggerRef}
        type="button"
        className={`erp-time-picker-trigger${value ? '' : ' is-empty'}`}
        onClick={() => !disabled && setOpen((current) => !current)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span>{displayValue}</span>
        <Clock size={14} />
      </button>
      {portal && createPortal(portal, document.body)}
    </div>
  )
}

export default TimePicker
